import React, { useState, useEffect, useCallback } from 'react';
import { observationApi, transportLegApi } from '../api/client';

// All price observations, with a filter on source and on reisonderdeel.
//
// By default the source is manual, because that is what you want to check
// after entering. But all sources are in the same table, and seeing what
// someone looked up next to what the pricing Excels produced is exactly what
// makes a series readable. Filtering on a reisonderdeel puts all priced rides
// from, say, Brussels to Paris in one column.
//
// Only manual entries can be deleted. The response says per row whether that
// is allowed, so this screen does not have to rebuild the rule.

const ALL = 'all';

const SOURCE_LABELS = {
  manual: 'handmatig',
  pricing_excel: 'prijs-Excels',
  ratehawk: 'RateHawk',
  eurostar: 'Eurostar',
  extension: 'browserextensie',
};

const formatMoney = (amount, currency) =>
  amount === null || amount === undefined
    ? ''
    : `${currency === 'EUR' ? '€' : currency} ${Number(amount).toLocaleString('nl-BE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const horizon = (queriedAt, travelDate) => {
  if (!queriedAt || !travelDate) return '';
  const days = Math.round((new Date(travelDate) - new Date(queriedAt.slice(0, 10))) / 86400000);
  return `${days} d`;
};

function ObservationList({ refreshKey }) {
  const [sources, setSources] = useState([]);
  const [legs, setLegs] = useState([]);
  const [source, setSource] = useState('manual');
  const [leg, setLeg] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    observationApi.sources().then((r) => setSources(r.data)).catch(() => {});
    transportLegApi
      .list()
      .then((r) => setLegs(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { source, limit: 500 };
      if (leg) params.transport_leg_id = Number(leg);
      const r = await observationApi.list(params);
      setRows(r.data);
      setMessage(null);
    } catch (error) {
      setMessage({ type: 'error', text: 'De waarnemingen konden niet opgehaald worden.' });
    } finally {
      setLoading(false);
    }
  }, [source, leg]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows, refreshKey]);

  const handleDelete = async (id) => {
    if (!window.confirm(`Waarneming ${id} verwijderen?`)) return;
    try {
      await observationApi.delete(id);
      fetchRows();
    } catch (error) {
      const detail = error.response && error.response.data && error.response.data.detail;
      setMessage({ type: 'error', text: detail || 'Verwijderen is niet gelukt.' });
    }
  };

  const total = sources.reduce((sum, s) => sum + s.observations, 0);
  const elsewhere = sources.filter((s) => s.source !== source && s.observations > 0);

  return (
    <div>
      <h2>Waarnemingen</h2>
      <p className="intro">
        Elke prijs die we ooit zagen, ongeacht wie hem aanleverde. Filter op een
        reisonderdeel om alle geprijsde ritten van bijvoorbeeld Brussel naar
        Parijs onder elkaar te krijgen. Alleen handmatig ingevoerde rijen kunnen
        weg; de rest hoort te groeien en niet te veranderen.
      </p>

      {message && <div className={`message ${message.type}`}>{message.text}</div>}

      <div className="filter-bar">
        <label>
          Bron
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="manual">
              handmatig
              {sources.find((s) => s.source === 'manual')
                ? ` (${sources.find((s) => s.source === 'manual').observations})`
                : ' (0)'}
            </option>
            {sources
              .filter((s) => s.source !== 'manual')
              .map((s) => (
                <option key={s.source} value={s.source}>
                  {SOURCE_LABELS[s.source] || s.source} ({s.observations})
                </option>
              ))}
            <option value={ALL}>alle bronnen ({total})</option>
          </select>
        </label>

        <label>
          Reisonderdeel
          <select value={leg} onChange={(e) => setLeg(e.target.value)}>
            <option value="">alle reisonderdelen</option>
            {legs.map((l) => (
              <option key={l.id} value={l.id}>
                {l.slug}
              </option>
            ))}
          </select>
        </label>

        <span className="count">
          {loading ? 'bezig met ophalen' : `${rows.length} waarnemingen`}
        </span>
      </div>

      {!loading && rows.length === 0 && (
        <p>
          Geen waarnemingen met deze filters.
          {elsewhere.length > 0 && (
            <>
              {' '}Er staan er wel{' '}
              {elsewhere.map((s, i) => (
                <React.Fragment key={s.source}>
                  {i > 0 && ', '}
                  <button className="link-button" onClick={() => setSource(s.source)}>
                    {s.observations} bij {SOURCE_LABELS[s.source] || s.source}
                  </button>
                </React.Fragment>
              ))}
              .
            </>
          )}
        </p>
      )}

      {!loading && rows.length > 0 && (
        <table className="insights-table">
          <thead>
            <tr>
              <th>Bron</th>
              <th>Reisonderdeel</th>
              <th>Opgezocht</th>
              <th>Reisdatum</th>
              <th className="number">Horizon</th>
              <th>Uitkomst</th>
              <th className="number">Prijs</th>
              <th className="number">Pers.</th>
              <th className="number">Per persoon</th>
              <th>Klasse</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id}>
                <td>{SOURCE_LABELS[o.source] || o.source}</td>
                <td>
                  {o.transport_leg_slug}
                  {o.price_type && (
                    <span className={`badge price-type-${o.price_type}`}>{o.price_type}</span>
                  )}
                </td>
                <td>{formatDate(o.queried_at)}</td>
                <td>{formatDate(o.travel_date)}</td>
                <td className="number">{horizon(o.queried_at, o.travel_date)}</td>
                <td>
                  {o.status === 'available' ? (
                    <span className="badge yes">prijs</span>
                  ) : (
                    <span className="badge warning">
                      {o.status === 'sold_out' ? 'uitverkocht' : 'geen resultaat'}
                    </span>
                  )}
                </td>
                <td className="number">{formatMoney(o.price, o.currency)}</td>
                <td className="number">{o.persons}</td>
                <td className="number">{formatMoney(o.price_per_person, o.currency)}</td>
                <td>{o.fare_class}</td>
                <td>
                  {o.deletable && (
                    <button className="btn-delete" onClick={() => handleDelete(o.id)}>
                      Verwijderen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ObservationList;
